import Link from "next/link";
import { useState, type FC } from "react";
import { Button } from "@components/Button";
import TelDialog from "@components/ShopDetail/TelDialog";
import TogglableIconButton from "@components/TogglableIconButton";
import { useShare } from "@hooks/useShare";
import useToggleFavoriteShop from "@hooks/useToggleFavoriteShop";
import { paths } from "@libs/paths";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import styles from "./index.module.css";

const CAN_RESERVE_TEL_BUTTON_LABEL = "電話";
const CANNOT_RESERVE_TEL_BUTTON_LABEL = "電話予約する";
const RESERVE_BUTTON_LABEL = "ネット予約する";
const UNAUTHENTICATED_RESERVE_BUTTON_PREFIX = "登録して";

type Props = {
  name: string;
  tel: string;
  telWithHyphen: string;
  canReserve: boolean;
  shopUuid: string;
  initialIsFavorite: boolean;
  showsUnauthenticatedReserveLabel: boolean;
};

const Footer: FC<Props> = ({
  tel,
  name,
  telWithHyphen,
  canReserve,
  shopUuid,
  initialIsFavorite,
  showsUnauthenticatedReserveLabel,
}) => {
  const telButtonLabel = canReserve
    ? CAN_RESERVE_TEL_BUTTON_LABEL
    : CANNOT_RESERVE_TEL_BUTTON_LABEL;
  const { toggle, isFavorite } = useToggleFavoriteShop(
    shopUuid,
    initialIsFavorite
  );

  const shareUrl = `${process.env.APP_HOST}${paths.shopDetail(shopUuid)}`;
  const { handleShareClick } = useShare(name, shareUrl);

  const handleToReservationClick = () => {
    GTMEvent(
      GTMEvents.toReservationFormPressed("ネット予約する", shopUuid || "")
    );
  };

  const [open, setOpen] = useState(false);

  const handleTelClick = () => {
    GTMEvent(GTMEvents.telButtonPressed(telButtonLabel, true));
    setOpen(true);
  };

  return (
    <>
      <div className={styles.btn}>
        <TogglableIconButton
          inactiveIconPath="/img/icon_share_white.svg"
          label={"シェア"}
          onClick={() => {
            GTMEvent(GTMEvents.shareButtonPressed("native"));
            handleShareClick();
          }}
          layout="spSmallBox"
        />
        <div className={styles.bookmark}>
          <TogglableIconButton
            inactiveIconPath="/img/icon_bookmark_outline.svg"
            activeIconPath="/img/icon_bookmark_fill.svg"
            label={"お気に入り"}
            isActive={isFavorite}
            onClick={toggle}
            layout="spSmallBox"
          />
        </div>
        {canReserve ? (
          <>
            <Button
              className={styles.telBorder}
              onClick={handleTelClick}
              styleType="outlined"
              colorType="primary"
            >
              <span>{CAN_RESERVE_TEL_BUTTON_LABEL}</span>
            </Button>
            <Link href={paths.shopRequestReservation(shopUuid)} legacyBehavior>
              <Button
                className={styles.reservation}
                onClick={handleToReservationClick}
                styleType="filled"
                colorType="primary"
              >
                <span>
                  {showsUnauthenticatedReserveLabel ? (
                    <>
                      {UNAUTHENTICATED_RESERVE_BUTTON_PREFIX}
                      <wbr />
                      {RESERVE_BUTTON_LABEL}
                    </>
                  ) : (
                    RESERVE_BUTTON_LABEL
                  )}
                </span>
              </Button>
            </Link>
          </>
        ) : (
          <Button
            className={styles.tel}
            onClick={handleTelClick}
            styleType="filled"
            colorType="primary"
          >
            <span>{CANNOT_RESERVE_TEL_BUTTON_LABEL}</span>
          </Button>
        )}
      </div>

      <TelDialog
        fromLabel={telButtonLabel}
        uuid={shopUuid}
        name={name}
        tel={tel}
        telWithHyphen={telWithHyphen}
        canReserve={canReserve}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default Footer;
